// Response validation for the agents-admin-api client (#343): a safety action (kill/pause) must only
// be confirmed by a well-formed acknowledgment, never by "any HTTP 2xx". Pure so it needs no Supabase env.
import { ACK_STATES, AGENT_STATES, RUN_STATES, type AgentsOverview } from "@/lib/agents-contract";

export type AgentsRoute = "/overview" | "/kill" | "/pause" | "/resume";

export class InvalidAgentsResponseError extends Error {
  constructor(route: AgentsRoute) {
    super(`invalid_response:${route}`);
  }
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

const isStr = (v: unknown): v is string => typeof v === "string";
const isStrOrNull = (v: unknown): boolean => v === null || isStr(v);
const isNumOrNull = (v: unknown): boolean => v === null || isNum(v);
const every = (v: unknown, test: (item: Record<string, unknown>) => boolean): boolean => Array.isArray(v) && v.every((i) => isObj(i) && test(i));

function isOverview(v: unknown): v is AgentsOverview {
  if (!isObj(v)) return false;
  const { kill, budget, runs_24h: runs } = v;
  return (
    typeof v.generated_at === "string" &&
    isObj(kill) && Number.isInteger(kill.epoch) && typeof kill.active === "boolean" &&
    isStrOrNull(kill.reason) && isStrOrNull(kill.set_at) && isStrOrNull(kill.set_by) &&
    isObj(budget) && [budget.cap_eur, budget.estimated_eur, budget.invoiced_eur, budget.infra_allowance_eur].every(isNum) &&
    every(v.leases, (l) => isStr(l.holder) && (ACK_STATES as readonly unknown[]).includes(l.ack_state) && isNumOrNull(l.epoch_seen) && isStrOrNull(l.last_ack_at) && isStrOrNull(l.lease_expires_at)) &&
    every(v.orgs, (o) => isStr(o.org_id) && isStr(o.name)) &&
    every(v.agents, (a) => isStr(a.agent_id) && isStr(a.name) && isStr(a.org_id) && isStr(a.org_name) && isStr(a.version) && (AGENT_STATES as readonly unknown[]).includes(a.state) && isStrOrNull(a.last_run_at)) &&
    isObj(runs) && isObj(runs.by_state) &&
    every(runs.items, (r) => isStr(r.run_id) && isStr(r.agent_id) && isStr(r.agent_name) && (RUN_STATES as readonly unknown[]).includes(r.state) && isStr(r.started_at) && isStrOrNull(r.finished_at) && isNum(r.cost_eur))
  );
}

/** The request body for pause/resume is needed to match the acknowledged agent id. */
export async function parseAgentsResponse<T>(res: Response, route: AgentsRoute, request?: { agent_id?: string }): Promise<T> {
  const payload: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    throw new Error(isObj(payload) && typeof payload.error === "string" ? payload.error : `HTTP ${res.status}`);
  }
  const ok =
    route === "/overview" ? isOverview(payload)
    : route === "/kill" ? isObj(payload) && Number.isInteger(payload.epoch) && (payload.epoch as number) >= 1 && payload.active === true
    : isObj(payload) && typeof request?.agent_id === "string" && payload.agent_id === request.agent_id && payload.state === (route === "/pause" ? "paused" : "active");
  if (!ok) throw new InvalidAgentsResponseError(route);
  return payload as T;
}
